import React, { FunctionComponent, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { SelectedAccessory } from '@upstart/harmony/lib/legos/accessoryTypes';
import { get, cloneDeep, set } from 'lodash';
import { Dimmer, Loader } from 'semantic-ui-react';
import { fmtPrice } from '@upstart/harmony/lib/formatter';
import * as changeLogHelper from '@upstart/harmony/lib/deskingoffer-changelog';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { getAccessoryPrice } from '@upstart/harmony/lib/accessory-utils';
import { isEmbeddedInCommandCenter } from 'util/commandCenter';
import { modalOverlayBackgroundColor } from 'util/constants';
import { Action, ModalType } from 'util/amplitudeEvents';
import CloseIcon from '../../Icons/Close';
import AccessoryList from '../../PencilEditor/AccessoryList';
import useAccessories from '../../PencilEditor/useAccessories';

const MODAL_STYLE = {
  content: {
    top: 'unset',
    bottom: 'unset',
    left: 'unset',
    right: 'unset',
    minWidth: '500px',
    maxWidth: '100vw',
    maxHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  overlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: modalOverlayBackgroundColor,
  },
};

interface Props {
  car: any;
  dealer: any;
  offer: DeskingOffer;
  isOpen: boolean;
  closeModal: () => void;
  onChange: (offer: DeskingOffer) => void;
  sendAmplitudeEvent?: (event: ModalType, action: Action) => void;
  disabled?: boolean;
}

const AccessoriesModal: FunctionComponent<Props> = props => {
  const {
    car,
    closeModal,
    dealer,
    isOpen,
    offer,
    onChange,
    sendAmplitudeEvent,
    disabled = false,
  } = props;
  const [selected, setSelected] = useState<SelectedAccessory[]>([]);
  const {
    fetching,
    accessories,
    toggleAccessory,
    totalAccessoryPrice,
  } = useAccessories({ dealer, car, selected });

  useEffect(() => {
    if (isOpen && offer) {
      // Update local state when modal opens or offer changes
      const offerAccessories = get(offer, ['data', 'selectedAccessories']);
      setSelected(offerAccessories || []);
    }
  }, [isOpen, offer]);

  const onSave = () => {
    const selectedAccessories = accessories
      .filter(a => selected.some(s => s.id === a.id))
      .map(a => ({
        id: a.id,
        name: a.name,
        taxed: a.taxed,
        price: getAccessoryPrice(a),
      }));

    const newOffer = cloneDeep(offer);
    const newChangeLog = changeLogHelper.updateAccessories(
      newOffer,
      selectedAccessories,
    );
    set(newOffer, 'changeLog', newChangeLog);
    set(newOffer, ['data', 'selectedAccessories'], selectedAccessories);
    onChange(newOffer);
    if (sendAmplitudeEvent) {
      sendAmplitudeEvent(ModalType.ACCESSORIES, Action.FINISH);
    }
    closeModal();
  };

  return (
    <Modal isOpen={isOpen} style={MODAL_STYLE as any}>
      <CloseIcon
        onClick={() => {
          if (sendAmplitudeEvent) {
            sendAmplitudeEvent(ModalType.ACCESSORIES, Action.CANCEL);
          }
          closeModal();
        }}
      />
      {fetching && (
        <Dimmer active inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
      )}
      <div className="protection-plans-modal-header">
        <div className="protection-plans-modal-title">Accessories</div>
        {!fetching && (
          <div className="protection-plans-modal-row">
            <div>Total Price {fmtPrice(totalAccessoryPrice)}</div>
            <button
              className="btn btn-primary"
              onClick={onSave}
              disabled={disabled || accessories.length === 0}
            >
              {isEmbeddedInCommandCenter() ? 'Save' : 'Save Accessories'}
            </button>
          </div>
        )}
      </div>
      <AccessoryList
        fetching={fetching}
        accessories={accessories}
        selectedAccessories={selected}
        toggleAccessory={accessory => setSelected(toggleAccessory(accessory))}
        disabled={disabled}
      />
    </Modal>
  );
};

export default AccessoriesModal;
